import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Label } from "@tellescope/ui";
import type { DatabaseRecord, DatabaseRecordValue } from "@tellescope/types-models";
import { useTellescopeMutation } from "@tellescope/tanstack-query";

export interface AddToDatabaseFormProps {
  databaseId: string;
  columns: string[];
  onCancel: () => void;
  onCreated: (record: DatabaseRecord) => void;
}

export const AddToDatabaseForm = ({ databaseId, columns, onCancel, onCreated }: AddToDatabaseFormProps) => {
  const { t } = useTranslation("forms");
  const [entries, setEntries] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const createRecord = useTellescopeMutation({
    model: "database_records",
    method: "createOne",
    onSuccess: (record) => onCreated(record),
    onError: () => setError(t("Could not add the record. Please try again.")),
  });

  const primaryColumn = columns[0];
  const canSubmit = !!primaryColumn && !!entries[primaryColumn]?.trim() && !createRecord.isPending;

  const handleSubmit = () => {
    setError(null);
    const values = columns.reduce<DatabaseRecordValue[]>((acc, column) => {
      const entry = entries[column]?.trim();
      if (entry) {
        acc.push({ type: "Text", label: column, value: entry });
      }
      return acc;
    }, []);

    createRecord.mutate({ databaseId, values });
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      {columns.map((column) => (
        <div key={column} className="space-y-1">
          <Label htmlFor={`${databaseId}-${column}`} required={column === primaryColumn}>
            {column}
          </Label>
          <Input
            id={`${databaseId}-${column}`}
            value={entries[column] ?? ""}
            onChange={(e) => setEntries((prev) => ({ ...prev, [column]: e.target.value }))}
          />
        </div>
      ))}

      {!!error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-2">
        <Button type="button" onClick={handleSubmit} disabled={!canSubmit} loading={createRecord.isPending}>
          {t("Add to database")}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
};
